/**
 * RegistrationFormBuilder.js
 * Admin UI for creating and configuring a league registration form.
 * Accessed from Dashboard "Manage Registration" button.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api, { sendPaymentLink, refundRegistration } from '../services/api';
import { useAuth } from '../context/AuthContext';
import UpgradePrompt from './UpgradePrompt';
import './RegistrationFormBuilder.css';

const APP_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/api', '')
  : window.location.origin;

const DEFAULT_FIELDS = [
  { label: 'Name', fieldType: 'text', required: true },
  { label: 'Email', fieldType: 'text', required: true },
  { label: 'Phone', fieldType: 'text', required: false },
];

const FIELD_TYPE_LABELS = {
  text: 'Text',
  dropdown: 'Dropdown',
  checkbox: 'Checkbox',
  waiver: 'Waiver',
  file_upload: 'File Upload',
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: '#6b7280', bg: '#f3f4f6' },
  open: { label: 'Open', color: '#16a34a', bg: '#dcfce7' },
  closed: { label: 'Closed', color: '#dc2626', bg: '#fee2e2' },
};

function Toast({ toast }) {
  if (!toast) return null;
  const bg = toast.type === 'error' ? '#dc2626' : '#16a34a';
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: bg, color: '#fff', padding: '10px 20px', borderRadius: 8,
      fontWeight: 600, fontSize: '0.9rem', zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
    }}>
      {toast.message}
    </div>
  );
}

function EmptyState({ onCreate, creating }) {
  return (
    <div className="reg-builder-empty">
      <div className="reg-builder-empty-icon">📋</div>
      <h3>No Registration Form Yet</h3>
      <p>Create a registration form so teams or players can sign up for this league.</p>
      <button
        className="reg-builder-btn-primary"
        onClick={onCreate}
        disabled={creating}
      >
        {creating ? 'Creating...' : 'Set Up Registration Form'}
      </button>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 12,
      fontSize: '0.8rem', fontWeight: 600,
      color: cfg.color, background: cfg.bg,
      border: `1px solid ${cfg.color}33`
    }}>
      {cfg.label}
    </span>
  );
}

// ─── Settings Tab ────────────────────────────────────────────────────────────

function SettingsTab({ form, leagueId, onFormUpdated, showToast }) {
  const [settings, setSettings] = useState({
    registrationMode: form.registrationMode || 'team',
    opensAt: form.opensAt ? form.opensAt.split('T')[0] : '',
    closesAt: form.closesAt ? form.closesAt.split('T')[0] : '',
    maxTeams: form.maxTeams ?? '',
    maxPlayersPerTeam: form.maxPlayersPerTeam ?? '',
    requirePayment: form.requirePayment || false,
    feeType: form.feeType || 'none',
    feeAmount: form.feeAmount ?? '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        registrationMode: settings.registrationMode,
        opensAt: settings.opensAt || null,
        closesAt: settings.closesAt || null,
        maxTeams: settings.maxTeams !== '' ? parseInt(settings.maxTeams, 10) : null,
        maxPlayersPerTeam: settings.maxPlayersPerTeam !== '' ? parseInt(settings.maxPlayersPerTeam, 10) : null,
        requirePayment: settings.requirePayment,
        feeType: settings.requirePayment ? settings.feeType : 'none',
        feeAmount: settings.requirePayment && settings.feeAmount !== '' ? parseFloat(settings.feeAmount) : null,
      };
      const result = await api.updateRegistrationForm(leagueId, payload);
      onFormUpdated(result.form);
      showToast('Settings saved');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="reg-builder-settings">
      <div className="reg-builder-section">
        <h3 className="reg-builder-section-title">Registration Mode</h3>
        <div className="reg-builder-mode-toggle">
          {['team', 'individual'].map(mode => (
            <button
              key={mode}
              className={`reg-builder-mode-btn ${settings.registrationMode === mode ? 'active' : ''}`}
              onClick={() => setSettings(s => ({ ...s, registrationMode: mode }))}
            >
              {mode === 'team' ? '👥 Team Registration' : '👤 Individual Registration'}
            </button>
          ))}
        </div>
        <p className="reg-builder-hint">
          {settings.registrationMode === 'team'
            ? 'Team captains register on behalf of their team.'
            : 'Individual players register themselves.'}
        </p>
      </div>

      <div className="reg-builder-section">
        <h3 className="reg-builder-section-title">Registration Window</h3>
        <div className="reg-builder-form-row">
          <label>Opens on</label>
          <input
            type="date"
            value={settings.opensAt}
            onChange={e => setSettings(s => ({ ...s, opensAt: e.target.value }))}
          />
        </div>
        <div className="reg-builder-form-row">
          <label>Closes on</label>
          <input
            type="date"
            value={settings.closesAt}
            onChange={e => setSettings(s => ({ ...s, closesAt: e.target.value }))}
          />
        </div>
      </div>

      <div className="reg-builder-section">
        <h3 className="reg-builder-section-title">Capacity Limits</h3>
        {settings.registrationMode === 'team' && (
          <>
            <div className="reg-builder-form-row">
              <label>Max teams</label>
              <input
                type="number"
                min="1"
                placeholder="Unlimited"
                value={settings.maxTeams}
                onChange={e => setSettings(s => ({ ...s, maxTeams: e.target.value }))}
              />
            </div>
            <div className="reg-builder-form-row">
              <label>Max players per team</label>
              <input
                type="number"
                min="1"
                placeholder="Unlimited"
                value={settings.maxPlayersPerTeam}
                onChange={e => setSettings(s => ({ ...s, maxPlayersPerTeam: e.target.value }))}
              />
            </div>
          </>
        )}
        {settings.registrationMode === 'individual' && (
          <p className="reg-builder-hint">
            Capacity limits are managed through team assignments in the Registrations tab.
          </p>
        )}
      </div>

      <div className="reg-builder-section">
        <h3 className="reg-builder-section-title">Payment</h3>
        <label className="reg-builder-toggle-row">
          <span>Require payment at registration</span>
          <span className="reg-builder-toggle-switch">
            <input
              type="checkbox"
              checked={settings.requirePayment}
              onChange={e => setSettings(s => ({ ...s, requirePayment: e.target.checked }))}
            />
            <span className="reg-builder-toggle-slider" />
          </span>
        </label>
        {settings.requirePayment && (
          <div className="reg-builder-payment-options">
            <div className="reg-builder-form-row">
              <label>Fee type</label>
              <div className="reg-builder-radio-group">
                {[['flat_team', 'Flat team fee'], ['per_player', 'Per-player fee']].map(([val, lbl]) => (
                  <label key={val} className="reg-builder-radio-label">
                    <input
                      type="radio"
                      name="feeType"
                      value={val}
                      checked={settings.feeType === val}
                      onChange={() => setSettings(s => ({ ...s, feeType: val }))}
                    />
                    {lbl}
                  </label>
                ))}
              </div>
            </div>
            <div className="reg-builder-form-row">
              <label>Fee amount ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={settings.feeAmount}
                onChange={e => setSettings(s => ({ ...s, feeAmount: e.target.value }))}
              />
            </div>
            <p className="reg-builder-hint" style={{ color: '#555' }}>
              Connect your Stripe account in <strong>Settings → Payments</strong> to collect fees automatically at registration.
              Admins can also mark registrations as paid manually (cash/check).
            </p>
          </div>
        )}
      </div>

      <div className="reg-builder-section-actions">
        <button
          className="reg-builder-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

// ─── Field Editor (inline form) ───────────────────────────────────────────────

function FieldEditor({ initial, onSave, onCancel, saving }) {
  const [field, setField] = useState(
    initial || { label: '', fieldType: 'text', required: false, options: [] }
  );
  const [newOption, setNewOption] = useState('');

  const addOption = () => {
    const opt = newOption.trim();
    if (!opt) return;
    setField(f => ({ ...f, options: [...(f.options || []), opt] }));
    setNewOption('');
  };

  const removeOption = (idx) => {
    setField(f => ({ ...f, options: f.options.filter((_, i) => i !== idx) }));
  };

  const valid = field.label.trim() &&
    (field.fieldType !== 'dropdown' || (field.options && field.options.length > 0));

  return (
    <div className="reg-builder-field-editor">
      <div className="reg-builder-form-row">
        <label>Label *</label>
        <input
          value={field.label}
          onChange={e => setField(f => ({ ...f, label: e.target.value }))}
          placeholder="e.g. Shirt Size"
        />
      </div>
      <div className="reg-builder-form-row">
        <label>Type</label>
        <select
          value={field.fieldType}
          onChange={e => setField(f => ({ ...f, fieldType: e.target.value, options: [] }))}
        >
          {Object.entries(FIELD_TYPE_LABELS).map(([val, lbl]) => (
            <option key={val} value={val}>{lbl}</option>
          ))}
        </select>
      </div>
      {field.fieldType === 'dropdown' && (
        <div className="reg-builder-form-row reg-builder-options-row">
          <label>Options *</label>
          <div className="reg-builder-options-list">
            {(field.options || []).map((opt, i) => (
              <div key={i} className="reg-builder-option-item">
                <span>{opt}</span>
                <button onClick={() => removeOption(i)} title="Remove">×</button>
              </div>
            ))}
            <div className="reg-builder-option-add">
              <input
                value={newOption}
                onChange={e => setNewOption(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addOption()}
                placeholder="Add option..."
              />
              <button onClick={addOption} disabled={!newOption.trim()}>Add</button>
            </div>
          </div>
        </div>
      )}
      <label className="reg-builder-toggle-row" style={{ marginTop: 8 }}>
        <span>Required</span>
        <span className="reg-builder-toggle-switch">
          <input
            type="checkbox"
            checked={field.required}
            onChange={e => setField(f => ({ ...f, required: e.target.checked }))}
          />
          <span className="reg-builder-toggle-slider" />
        </span>
      </label>
      <div className="reg-builder-field-editor-actions">
        <button
          className="reg-builder-btn-primary"
          onClick={() => onSave(field)}
          disabled={!valid || saving}
        >
          {saving ? 'Saving...' : initial ? 'Update Field' : 'Add Field'}
        </button>
        <button className="reg-builder-btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Fields Tab ───────────────────────────────────────────────────────────────

function FieldsTab({ form, leagueId, onFormUpdated, showToast }) {
  const [fields, setFields] = useState(form.fields || []);
  const [showAddEditor, setShowAddEditor] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleAdd = async (fieldData) => {
    setSaving(true);
    try {
      const result = await api.addRegistrationField(leagueId, {
        label: fieldData.label.trim(),
        fieldType: fieldData.fieldType,
        required: fieldData.required,
        options: fieldData.fieldType === 'dropdown' ? fieldData.options : null,
      });
      const newFields = [...fields, result.field];
      setFields(newFields);
      onFormUpdated({ ...form, fields: newFields });
      setShowAddEditor(false);
      showToast('Field added');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (fieldData) => {
    setSaving(true);
    try {
      const result = await api.updateRegistrationField(leagueId, editingFieldId, {
        label: fieldData.label.trim(),
        fieldType: fieldData.fieldType,
        required: fieldData.required,
        options: fieldData.fieldType === 'dropdown' ? fieldData.options : null,
      });
      const newFields = fields.map(f => f.id === editingFieldId ? result.field : f);
      setFields(newFields);
      onFormUpdated({ ...form, fields: newFields });
      setEditingFieldId(null);
      showToast('Field updated');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('Delete this field?')) return;
    try {
      await api.deleteRegistrationField(leagueId, fieldId);
      const newFields = fields.filter(f => f.id !== fieldId);
      setFields(newFields);
      onFormUpdated({ ...form, fields: newFields });
      showToast('Field deleted');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleMove = async (fromIdx, direction) => {
    const toIdx = fromIdx + direction;
    if (toIdx < 0 || toIdx >= fields.length) return;
    const newFields = [...fields];
    [newFields[fromIdx], newFields[toIdx]] = [newFields[toIdx], newFields[fromIdx]];
    setFields(newFields);
    try {
      await api.reorderRegistrationFields(leagueId, newFields.map(f => f.id));
      onFormUpdated({ ...form, fields: newFields });
    } catch (err) {
      showToast(err.message, 'error');
      setFields(fields); // revert
    }
  };

  return (
    <div className="reg-builder-fields">
      <p className="reg-builder-hint" style={{ marginBottom: 16 }}>
        Default fields (Name, Email, Phone) are always included and cannot be removed.
      </p>

      <div className="reg-builder-default-fields">
        {DEFAULT_FIELDS.map(f => (
          <div key={f.label} className="reg-builder-field-row reg-builder-field-row-default">
            <span className="reg-builder-field-label">{f.label}</span>
            <span className="reg-builder-field-type">{FIELD_TYPE_LABELS[f.fieldType]}</span>
            {f.required && <span className="reg-builder-badge-required">Required</span>}
            <span className="reg-builder-default-tag">Default</span>
          </div>
        ))}
      </div>

      {fields.length > 0 && (
        <div className="reg-builder-custom-fields">
          <h4 className="reg-builder-fields-subheader">Custom Fields</h4>
          {fields.map((field, idx) => (
            <div key={field.id}>
              {editingFieldId === field.id ? (
                <FieldEditor
                  initial={{ ...field, options: field.options || [] }}
                  onSave={handleEdit}
                  onCancel={() => setEditingFieldId(null)}
                  saving={saving}
                />
              ) : (
                <div className="reg-builder-field-row">
                  <div className="reg-builder-field-reorder">
                    <button
                      onClick={() => handleMove(idx, -1)}
                      disabled={idx === 0}
                      title="Move up"
                      className="reg-builder-btn-icon"
                    >▲</button>
                    <button
                      onClick={() => handleMove(idx, 1)}
                      disabled={idx === fields.length - 1}
                      title="Move down"
                      className="reg-builder-btn-icon"
                    >▼</button>
                  </div>
                  <span className="reg-builder-field-label">{field.label}</span>
                  <span className="reg-builder-field-type">{FIELD_TYPE_LABELS[field.fieldType] || field.fieldType}</span>
                  {field.required && <span className="reg-builder-badge-required">Required</span>}
                  {field.fieldType === 'dropdown' && field.options?.length > 0 && (
                    <span className="reg-builder-hint" style={{ fontSize: '0.75rem' }}>
                      {field.options.join(', ')}
                    </span>
                  )}
                  <div className="reg-builder-field-actions">
                    <button
                      className="reg-builder-btn-secondary"
                      onClick={() => { setEditingFieldId(field.id); setShowAddEditor(false); }}
                    >
                      Edit
                    </button>
                    <button
                      className="reg-builder-btn-danger"
                      onClick={() => handleDelete(field.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showAddEditor ? (
        <div style={{ marginTop: 16 }}>
          <h4 className="reg-builder-fields-subheader">New Field</h4>
          <FieldEditor
            onSave={handleAdd}
            onCancel={() => setShowAddEditor(false)}
            saving={saving}
          />
        </div>
      ) : (
        !editingFieldId && (
          <button
            className="reg-builder-btn-secondary"
            style={{ marginTop: 16 }}
            onClick={() => setShowAddEditor(true)}
          >
            + Add Custom Field
          </button>
        )
      )}
    </div>
  );
}

// ─── Registrations Tab ───────────────────────────────────────────────────────

const PAYMENT_STATUS_LABELS = {
  not_required: 'N/A',
  pending: 'Pending',
  paid: 'Paid',
  refunded: 'Refunded',
};

const PAYMENT_STATUS_COLORS = {
  not_required: { color: '#6b7280', bg: '#f3f4f6' },
  pending: { color: '#b45309', bg: '#fef3c7' },
  paid: { color: '#16a34a', bg: '#dcfce7' },
  refunded: { color: '#7c3aed', bg: '#ede9fe' },
};

function PaymentBadge({ status }) {
  const cfg = PAYMENT_STATUS_COLORS[status] || PAYMENT_STATUS_COLORS.not_required;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 10,
      fontSize: '0.75rem', fontWeight: 600, color: cfg.color, background: cfg.bg,
      border: `1px solid ${cfg.color}33`
    }}>
      {PAYMENT_STATUS_LABELS[status] || status}
    </span>
  );
}

function EditRegistrationModal({ registration, teams, leagueId, onSave, onClose, showToast }) {
  const [teamId, setTeamId] = useState(registration.teamId || '');
  const [paymentStatus, setPaymentStatus] = useState(registration.paymentStatus || 'not_required');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {
        paymentStatus,
        teamId: teamId || null,
      };
      const result = await api.updateRegistration(leagueId, registration.id, updates);
      onSave(result.registration);
      showToast('Registration updated');
      onClose();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
    }}>
      <div style={{
        background: 'var(--color-card, #fff)', borderRadius: 12, padding: 24,
        width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
      }}>
        <h3 style={{ margin: '0 0 4px 0', color: 'var(--color-text, #111)' }}>Edit Registration</h3>
        <p style={{ margin: '0 0 20px 0', color: 'var(--color-text-secondary, #6b7280)', fontSize: '0.9rem' }}>
          {registration.registrantName} · {registration.registrantEmail}
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: 'var(--color-text, #111)', fontSize: '0.875rem' }}>
            Team Assignment
          </label>
          <select
            value={teamId}
            onChange={e => setTeamId(e.target.value)}
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 6,
              border: '1px solid var(--color-input-border, #d1d5db)',
              background: 'var(--color-input-bg, #fff)', color: 'var(--color-text, #111)',
              fontSize: '0.9rem'
            }}
          >
            <option value="">Unassigned</option>
            {(teams || []).map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: 'var(--color-text, #111)', fontSize: '0.875rem' }}>
            Payment Status
          </label>
          <select
            value={paymentStatus}
            onChange={e => setPaymentStatus(e.target.value)}
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 6,
              border: '1px solid var(--color-input-border, #d1d5db)',
              background: 'var(--color-input-bg, #fff)', color: 'var(--color-text, #111)',
              fontSize: '0.9rem'
            }}
          >
            {Object.entries(PAYMENT_STATUS_LABELS).map(([val, lbl]) => (
              <option key={val} value={val}>{lbl}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px', borderRadius: 6, border: '1px solid var(--color-input-border, #d1d5db)',
              background: 'transparent', color: 'var(--color-text, #111)', cursor: 'pointer', fontWeight: 500
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 16px', borderRadius: 6, border: 'none',
              background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff',
              cursor: 'pointer', fontWeight: 600, opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RegistrationsTab({ form, leagueId, showToast }) {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [stripeReady, setStripeReady] = useState(false);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [editingReg, setEditingReg] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [sendingPaymentLink, setSendingPaymentLink] = useState(null); // registrationId
  const [refunding, setRefunding] = useState(null); // registrationId
  const [markingPaid, setMarkingPaid] = useState(null); // registrationId

  // Bulk assignment state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [assignTeamId, setAssignTeamId] = useState('');
  const [assigning, setAssigning] = useState(false);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const params = {};
      if (paymentFilter) params.status = paymentFilter;
      if (teamFilter === 'unassigned') params.team = 'unassigned';
      const result = await api.getRegistrations(leagueId, params);
      setRegistrations(result.registrations || []);
      setStats(result.stats || {});
      setStripeReady(!!result.stripeReady);

      // Collect unique teams from registrations for assignment dropdowns
      const teamMap = {};
      (result.registrations || []).forEach(r => {
        if (r.team) teamMap[r.team.id] = r.team;
      });
      setTeams(Object.values(teamMap));
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, [leagueId, paymentFilter, teamFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (reg) => {
    if (!window.confirm(`Delete registration for ${reg.registrantName}? A cancellation email will be sent.`)) return;
    try {
      await api.deleteRegistration(leagueId, reg.id);
      setRegistrations(prev => prev.filter(r => r.id !== reg.id));
      setSelectedIds(prev => { const s = new Set(prev); s.delete(reg.id); return s; });
      showToast('Registration deleted');
      fetchRegistrations(); // refresh stats
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleEditSave = (updatedReg) => {
    setRegistrations(prev => prev.map(r => r.id === updatedReg.id ? { ...r, ...updatedReg } : r));
    fetchRegistrations(); // refresh stats
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.exportRegistrations(leagueId);
      const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers?.['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : 'registrations.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleBulkAssign = async () => {
    if (!assignTeamId || selectedIds.size === 0) return;
    setAssigning(true);
    let successCount = 0;
    try {
      for (const id of selectedIds) {
        try {
          const result = await api.updateRegistration(leagueId, id, { teamId: assignTeamId });
          setRegistrations(prev => prev.map(r => r.id === id ? { ...r, ...result.registration } : r));
          successCount++;
        } catch {
          // continue with others
        }
      }
      showToast(`Assigned ${successCount} registrant${successCount !== 1 ? 's' : ''} to team`);
      setSelectedIds(new Set());
      setAssignTeamId('');
      fetchRegistrations();
    } finally {
      setAssigning(false);
    }
  };

  const handleAutoAssign = async () => {
    const unassigned = registrations.filter(r => !r.teamId);
    if (unassigned.length === 0 || teams.length === 0) {
      showToast('No unassigned registrants or no teams available', 'error');
      return;
    }
    if (!window.confirm(`Auto-assign ${unassigned.length} unassigned registrant${unassigned.length !== 1 ? 's' : ''} evenly across ${teams.length} team${teams.length !== 1 ? 's' : ''}?`)) return;

    setAssigning(true);
    let successCount = 0;
    try {
      for (let i = 0; i < unassigned.length; i++) {
        const team = teams[i % teams.length];
        try {
          const result = await api.updateRegistration(leagueId, unassigned[i].id, { teamId: team.id });
          setRegistrations(prev => prev.map(r => r.id === unassigned[i].id ? { ...r, ...result.registration } : r));
          successCount++;
        } catch {
          // continue
        }
      }
      showToast(`Auto-assigned ${successCount} registrant${successCount !== 1 ? 's' : ''}`);
      fetchRegistrations();
    } finally {
      setAssigning(false);
    }
  };

  const handleSendPaymentLink = async (reg) => {
    setSendingPaymentLink(reg.id);
    try {
      await sendPaymentLink(leagueId, reg.id);
      showToast(`Payment link sent to ${reg.registrantEmail}`);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSendingPaymentLink(null);
    }
  };

  const handleRefund = async (reg) => {
    if (!window.confirm(
      `Issue a refund of $${parseFloat(reg.paymentAmount || 0).toFixed(2)} to ${reg.registrantName}?\n\nThis will process through Stripe and cannot be undone.`
    )) return;
    setRefunding(reg.id);
    try {
      await refundRegistration(leagueId, reg.id);
      setRegistrations(prev => prev.map(r => r.id === reg.id ? { ...r, paymentStatus: 'refunded' } : r));
      showToast('Refund processed successfully');
      fetchRegistrations();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setRefunding(null);
    }
  };

  const handleMarkAsPaid = async (reg) => {
    setMarkingPaid(reg.id);
    try {
      const result = await api.updateRegistration(leagueId, reg.id, { paymentStatus: 'paid' });
      setRegistrations(prev => prev.map(r => r.id === reg.id ? { ...r, ...result.registration } : r));
      showToast(`${reg.registrantName} marked as paid`);
      fetchRegistrations();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setMarkingPaid(null);
    }
  };

  // Client-side search filter
  const filtered = registrations.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.registrantName || '').toLowerCase().includes(q) ||
           (r.registrantEmail || '').toLowerCase().includes(q);
  });

  const unassignedList = registrations.filter(r => !r.teamId);
  const isIndividualMode = form.registrationMode === 'individual';

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(r => r.id)));
    }
  };

  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-secondary, #6b7280)' }}>Loading registrations...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {editingReg && (
        <EditRegistrationModal
          registration={editingReg}
          teams={teams}
          leagueId={leagueId}
          onSave={handleEditSave}
          onClose={() => setEditingReg(null)}
          showToast={showToast}
        />
      )}

      {/* Overview bar */}
      {stats && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12
          }}>
            {[
              { label: 'Total', value: stats.total || 0, color: '#667eea' },
              { label: 'Captains', value: stats.teamCount || 0, color: '#764ba2' },
              { label: 'Players', value: stats.individualCount || 0, color: '#0ea5e9' },
              { label: 'Unassigned', value: stats.unassignedCount || 0, color: '#f59e0b' },
              { label: 'Paid', value: stats.paidCount || 0, color: '#16a34a' },
              { label: 'Pending $', value: stats.pendingCount || 0, color: '#dc2626' },
            ].map(card => (
              <div key={card.label} style={{
                background: 'var(--color-card, #fff)', border: '1px solid var(--color-border, #e5e7eb)',
                borderRadius: 8, padding: '12px 16px', textAlign: 'center',
                borderTop: `3px solid ${card.color}`
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: card.color }}>{card.value}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary, #6b7280)', marginTop: 2 }}>{card.label}</div>
              </div>
            ))}
          </div>

          {/* Financial summary — only shown when payment is configured */}
          {form.requirePayment && (stats.totalCollected > 0 || stats.pendingTotal > 0) && (
            <div style={{
              background: 'var(--color-card, #fff)', border: '1px solid var(--color-border, #e5e7eb)',
              borderRadius: 10, padding: '14px 20px',
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16
            }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary, #6b7280)', marginBottom: 2, fontWeight: 600 }}>
                  TOTAL COLLECTED
                </div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#16a34a' }}>
                  ${(stats.totalCollected || 0).toFixed(2)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary, #6b7280)', marginBottom: 2, fontWeight: 600 }}>
                  PLATFORM FEES (1%)
                </div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#667eea' }}>
                  ${(stats.platformFees || 0).toFixed(2)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary, #6b7280)', marginBottom: 2, fontWeight: 600 }}>
                  NET PAYOUT
                </div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111' }}>
                  ${((stats.totalCollected || 0) - (stats.platformFees || 0)).toFixed(2)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary, #6b7280)', marginBottom: 2, fontWeight: 600 }}>
                  PENDING PAYMENTS
                </div>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#dc2626' }}>
                  ${(stats.pendingTotal || 0).toFixed(2)}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Team assignment panel — individual mode only */}
      {isIndividualMode && unassignedList.length > 0 && (
        <div style={{
          background: 'var(--color-card, #fff)', border: '1px solid var(--color-border, #e5e7eb)',
          borderRadius: 10, padding: 16
        }}>
          <h4 style={{ margin: '0 0 12px 0', color: 'var(--color-text, #111)', fontSize: '0.95rem' }}>
            Team Assignment — {unassignedList.length} Unassigned
          </h4>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary, #6b7280)' }}>
              {selectedIds.size} selected
            </span>
            <select
              value={assignTeamId}
              onChange={e => setAssignTeamId(e.target.value)}
              style={{
                padding: '6px 10px', borderRadius: 6,
                border: '1px solid var(--color-input-border, #d1d5db)',
                background: 'var(--color-input-bg, #fff)', color: 'var(--color-text, #111)', fontSize: '0.875rem'
              }}
            >
              <option value="">Select team...</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button
              onClick={handleBulkAssign}
              disabled={assigning || !assignTeamId || selectedIds.size === 0}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none',
                background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff',
                cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
                opacity: (assigning || !assignTeamId || selectedIds.size === 0) ? 0.5 : 1
              }}
            >
              {assigning ? 'Assigning...' : 'Assign Selected'}
            </button>
            <button
              onClick={handleAutoAssign}
              disabled={assigning}
              style={{
                padding: '6px 14px', borderRadius: 6,
                border: '1px solid var(--color-input-border, #d1d5db)',
                background: 'transparent', color: 'var(--color-text, #111)',
                cursor: 'pointer', fontWeight: 500, fontSize: '0.875rem'
              }}
            >
              Auto-assign
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, minWidth: 180, padding: '8px 12px', borderRadius: 6,
            border: '1px solid var(--color-input-border, #d1d5db)',
            background: 'var(--color-input-bg, #fff)', color: 'var(--color-text, #111)', fontSize: '0.9rem'
          }}
        />
        <select
          value={paymentFilter}
          onChange={e => setPaymentFilter(e.target.value)}
          style={{
            padding: '8px 10px', borderRadius: 6,
            border: '1px solid var(--color-input-border, #d1d5db)',
            background: 'var(--color-input-bg, #fff)', color: 'var(--color-text, #111)', fontSize: '0.875rem'
          }}
        >
          <option value="">All payments</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="not_required">N/A</option>
        </select>
        <select
          value={teamFilter}
          onChange={e => setTeamFilter(e.target.value)}
          style={{
            padding: '8px 10px', borderRadius: 6,
            border: '1px solid var(--color-input-border, #d1d5db)',
            background: 'var(--color-input-bg, #fff)', color: 'var(--color-text, #111)', fontSize: '0.875rem'
          }}
        >
          <option value="">All teams</option>
          <option value="unassigned">Unassigned only</option>
        </select>
        <button
          onClick={handleExport}
          disabled={exporting || registrations.length === 0}
          style={{
            padding: '8px 14px', borderRadius: 6,
            border: '1px solid var(--color-input-border, #d1d5db)',
            background: 'transparent', color: 'var(--color-text, #111)',
            cursor: 'pointer', fontWeight: 500, fontSize: '0.875rem',
            opacity: exporting || registrations.length === 0 ? 0.5 : 1
          }}
        >
          {exporting ? 'Exporting...' : '⬇ Export CSV'}
        </button>
      </div>

      {/* Registrations table */}
      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '48px 16px',
          color: 'var(--color-text-secondary, #6b7280)', fontSize: '0.95rem'
        }}>
          {registrations.length === 0
            ? 'No registrations yet.'
            : 'No registrations match your filters.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem',
            background: 'var(--color-card, #fff)', borderRadius: 10, overflow: 'hidden',
            border: '1px solid var(--color-border, #e5e7eb)'
          }}>
            <thead>
              <tr style={{ background: 'var(--color-table-header, #f9fafb)' }}>
                {isIndividualMode && (
                  <th style={{ padding: '10px 12px', textAlign: 'left', width: 36 }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filtered.length && filtered.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}
                {['Name', 'Email', 'Team', 'Role', 'Payment', 'Registered', ''].map(h => (
                  <th key={h} style={{
                    padding: '10px 12px', textAlign: 'left', fontWeight: 600,
                    color: 'var(--color-text-secondary, #6b7280)',
                    borderBottom: '1px solid var(--color-border, #e5e7eb)',
                    whiteSpace: 'nowrap'
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((reg, idx) => (
                <tr
                  key={reg.id}
                  style={{
                    background: idx % 2 === 0
                      ? 'var(--color-table-bg, #fff)'
                      : 'var(--color-table-stripe, #f9fafb)',
                    borderBottom: '1px solid var(--color-border, #e5e7eb)'
                  }}
                >
                  {isIndividualMode && (
                    <td style={{ padding: '10px 12px' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(reg.id)}
                        onChange={() => toggleSelect(reg.id)}
                      />
                    </td>
                  )}
                  <td style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--color-text, #111)' }}>
                    {reg.registrantName}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary, #6b7280)' }}>
                    {reg.registrantEmail}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--color-text, #111)' }}>
                    {reg.team?.name || <span style={{ color: '#f59e0b', fontSize: '0.8rem' }}>Unassigned</span>}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--color-text, #111)', textTransform: 'capitalize' }}>
                    {reg.role || 'player'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <PaymentBadge status={reg.paymentStatus} />
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--color-text-secondary, #6b7280)', whiteSpace: 'nowrap' }}>
                    {reg.registeredAt ? new Date(reg.registeredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        onClick={() => setEditingReg(reg)}
                        style={{
                          padding: '4px 10px', borderRadius: 5, border: '1px solid var(--color-input-border, #d1d5db)',
                          background: 'transparent', color: 'var(--color-text, #111)',
                          cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500
                        }}
                      >
                        Edit
                      </button>

                      {/* Send Payment Link — pending + payment required + Stripe ready */}
                      {reg.paymentStatus === 'pending' && form.requirePayment && stripeReady && (
                        <button
                          onClick={() => handleSendPaymentLink(reg)}
                          disabled={sendingPaymentLink === reg.id}
                          title="Email a Stripe Checkout link to this registrant"
                          style={{
                            padding: '4px 10px', borderRadius: 5, border: '1px solid #a78bfa',
                            background: 'transparent', color: '#7c3aed',
                            cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500,
                            opacity: sendingPaymentLink === reg.id ? 0.6 : 1
                          }}
                        >
                          {sendingPaymentLink === reg.id ? 'Sending…' : 'Send Link'}
                        </button>
                      )}

                      {/* Mark as Paid — pending + payment required (cash/check) */}
                      {reg.paymentStatus === 'pending' && form.requirePayment && (
                        <button
                          onClick={() => handleMarkAsPaid(reg)}
                          disabled={markingPaid === reg.id}
                          title="Mark as paid manually (cash or check)"
                          style={{
                            padding: '4px 10px', borderRadius: 5, border: '1px solid #86efac',
                            background: 'transparent', color: '#16a34a',
                            cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500,
                            opacity: markingPaid === reg.id ? 0.6 : 1
                          }}
                        >
                          {markingPaid === reg.id ? 'Saving…' : 'Mark Paid'}
                        </button>
                      )}

                      {/* Refund — paid + Stripe payment ID exists */}
                      {reg.paymentStatus === 'paid' && reg.stripePaymentId && (
                        <button
                          onClick={() => handleRefund(reg)}
                          disabled={refunding === reg.id}
                          title="Issue a Stripe refund"
                          style={{
                            padding: '4px 10px', borderRadius: 5, border: '1px solid #fca5a5',
                            background: 'transparent', color: '#dc2626',
                            cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500,
                            opacity: refunding === reg.id ? 0.6 : 1
                          }}
                        >
                          {refunding === reg.id ? 'Refunding…' : 'Refund'}
                        </button>
                      )}

                      <button
                        onClick={() => handleDelete(reg)}
                        style={{
                          padding: '4px 10px', borderRadius: 5, border: '1px solid #fca5a5',
                          background: 'transparent', color: '#dc2626',
                          cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--color-text-secondary, #6b7280)' }}>
            Showing {filtered.length} of {registrations.length} registration{registrations.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Preview Tab ─────────────────────────────────────────────────────────────

function PreviewTab({ form }) {
  const allFields = [...DEFAULT_FIELDS, ...(form.fields || [])];
  const modeName = form.registrationMode === 'individual' ? 'Individual Registration' : 'Team Registration';

  return (
    <div className="reg-builder-preview">
      <p className="reg-builder-hint" style={{ marginBottom: 16 }}>
        This is how the form will appear to registrants (read-only preview).
      </p>
      <div className="reg-builder-preview-form">
        <h3 className="reg-builder-preview-title">{modeName}</h3>
        {allFields.map((field, i) => (
          <div key={i} className="reg-builder-preview-field">
            <label className="reg-builder-preview-label">
              {field.label}
              {field.required && <span style={{ color: '#dc2626', marginLeft: 3 }}>*</span>}
            </label>
            {field.fieldType === 'text' && (
              <input disabled placeholder={`Enter ${field.label.toLowerCase()}`} />
            )}
            {field.fieldType === 'dropdown' && (
              <select disabled>
                <option value="">Select an option...</option>
                {(field.options || []).map((opt, j) => (
                  <option key={j} value={opt}>{opt}</option>
                ))}
              </select>
            )}
            {field.fieldType === 'checkbox' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" disabled />
                <span>{field.label}</span>
              </label>
            )}
            {field.fieldType === 'waiver' && (
              <div className="reg-builder-preview-waiver">
                <p>I agree to the terms and conditions.</p>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" disabled />
                  <span>I agree</span>
                </label>
              </div>
            )}
            {field.fieldType === 'file_upload' && (
              <input type="file" disabled />
            )}
          </div>
        ))}
        {form.requirePayment && (
          <div className="reg-builder-preview-field">
            <label className="reg-builder-preview-label">
              Payment
              <span style={{ color: '#dc2626', marginLeft: 3 }}>*</span>
            </label>
            <div className="reg-builder-preview-payment">
              💳 {form.feeType === 'per_player' ? 'Per-player fee' : 'Team fee'}:&nbsp;
              <strong>${form.feeAmount ? parseFloat(form.feeAmount).toFixed(2) : '—'}</strong>
            </div>
          </div>
        )}
        <button className="reg-builder-btn-primary" disabled>
          {form.registrationMode === 'individual' ? 'Register' : 'Register Team'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function RegistrationFormBuilder({ leagueId, onBack }) {
  const { user } = useAuth();
  const userPlan = user?.plan || 'free';
  const [league, setLeague] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [toast, setToast] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const leagueResult = await api.getLeague(leagueId);
      setLeague(leagueResult.league || leagueResult);
      try {
        const formResult = await api.getRegistrationForm(leagueId);
        setForm(formResult.form);
      } catch (err) {
        // 404 = no form yet, that's ok
        if (!err.message.includes('No registration form')) {
          showToast(err.message, 'error');
        }
        setForm(null);
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [leagueId, showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const result = await api.createRegistrationForm(leagueId, { registrationMode: 'team' });
      setForm({ ...result.form, fields: [] });
      showToast('Registration form created');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusLoading(true);
    try {
      const result = await api.updateRegistrationFormStatus(leagueId, newStatus);
      setForm(prev => ({ ...prev, ...result.form }));
      const msgs = { open: 'Registration is now open!', closed: 'Registration closed.', draft: 'Reverted to draft.' };
      showToast(msgs[newStatus] || 'Status updated');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setStatusLoading(false);
    }
  };

  const copyLink = () => {
    const link = `${APP_URL}/register/${leagueId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="reg-builder-container">
        <div className="reg-builder-loading">Loading...</div>
      </div>
    );
  }

  const leagueName = league?.name || 'League';
  const registrationLink = `${APP_URL}/register/${leagueId}`;

  const TABS = [
    { id: 'settings', label: 'Settings' },
    { id: 'fields', label: `Fields (${(form?.fields?.length || 0) + DEFAULT_FIELDS.length})` },
    { id: 'preview', label: 'Preview' },
    { id: 'registrations', label: 'Registrations' },
  ];

  if (userPlan === 'free') {
    return (
      <div className="reg-builder-container">
        <div className="reg-builder-header">
          <button className="reg-builder-back-btn" onClick={onBack}>← Back</button>
          <div className="reg-builder-header-title">
            <h2>Registration Form</h2>
          </div>
        </div>
        <UpgradePrompt
          requiredPlan="starter"
          feature="Online Registration"
        />
      </div>
    );
  }

  return (
    <div className="reg-builder-container">
      <Toast toast={toast} />

      {/* Header */}
      <div className="reg-builder-header">
        <button className="reg-builder-back-btn" onClick={onBack}>← Back</button>
        <div className="reg-builder-header-title">
          <h2>Registration Form</h2>
          <span className="reg-builder-league-name">{leagueName}</span>
        </div>
        {form && <StatusBadge status={form.status} />}
      </div>

      {!form ? (
        <EmptyState onCreate={handleCreate} creating={creating} />
      ) : (
        <>
          {/* Status action bar */}
          <div className="reg-builder-status-bar">
            <div className="reg-builder-status-info">
              <StatusBadge status={form.status} />
              {form.opensAt && (
                <span className="reg-builder-hint">
                  Opens {new Date(form.opensAt).toLocaleDateString()}
                </span>
              )}
              {form.closesAt && (
                <span className="reg-builder-hint">
                  · Closes {new Date(form.closesAt).toLocaleDateString()}
                </span>
              )}
            </div>
            <div className="reg-builder-status-actions">
              {form.status === 'draft' && (
                <button
                  className="reg-builder-btn-success"
                  onClick={() => handleStatusChange('open')}
                  disabled={statusLoading}
                >
                  {statusLoading ? 'Opening...' : 'Open Registration'}
                </button>
              )}
              {form.status === 'open' && (
                <button
                  className="reg-builder-btn-danger"
                  onClick={() => handleStatusChange('closed')}
                  disabled={statusLoading}
                >
                  {statusLoading ? 'Closing...' : 'Close Registration'}
                </button>
              )}
              {form.status === 'closed' && (
                <button
                  className="reg-builder-btn-secondary"
                  onClick={() => handleStatusChange('open')}
                  disabled={statusLoading}
                >
                  {statusLoading ? 'Re-opening...' : 'Re-open Registration'}
                </button>
              )}
            </div>
          </div>

          {/* Shareable link — shown when form is open */}
          {form.status === 'open' && (
            <div className="reg-builder-share-bar">
              <span className="reg-builder-share-label">🔗 Registration link:</span>
              <code className="reg-builder-share-link">{registrationLink}</code>
              <button
                className="reg-builder-btn-copy"
                onClick={copyLink}
              >
                {copiedLink ? 'Copied!' : 'Copy'}
              </button>
            </div>
          )}

          {/* Draft link preview */}
          {form.status === 'draft' && (
            <div className="reg-builder-share-bar" style={{ opacity: 0.6 }}>
              <span className="reg-builder-share-label">🔗 Registration link (not yet active):</span>
              <code className="reg-builder-share-link">{registrationLink}</code>
            </div>
          )}

          {/* Tabs */}
          <div className="reg-builder-tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                className={`reg-builder-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="reg-builder-tab-content">
            {activeTab === 'settings' && (
              <SettingsTab
                form={form}
                leagueId={leagueId}
                onFormUpdated={(updated) => setForm(prev => ({ ...prev, ...updated }))}
                showToast={showToast}
              />
            )}
            {activeTab === 'fields' && (
              <FieldsTab
                form={form}
                leagueId={leagueId}
                onFormUpdated={(updated) => setForm(prev => ({ ...prev, ...updated }))}
                showToast={showToast}
              />
            )}
            {activeTab === 'preview' && (
              <PreviewTab form={form} />
            )}
            {activeTab === 'registrations' && (
              <RegistrationsTab
                form={form}
                leagueId={leagueId}
                showToast={showToast}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
